import React, { useCallback, useEffect, useState } from "react";
import { Check, Mail, Phone, RefreshCw, Send } from "lucide-react";
import { api } from "../console/api";
import { Groupe, Segments, Vide, Badge } from "../espace/ui";
import { dateCourte, tel } from "./outils";

const COULEUR = { rouge: "#DC2626", ambre: "#F59E0B", bleu: "#2563EB" };

/* Un groupe d'alertes de la console. « Vu » range une ligne ; ce qui demande un écran
   complet (générer une facture, fusionner deux fiches) se fait sur ordinateur. */
/** Où mène une alerte sur le téléphone : la fiche de l'élève, la séance, les messages… */
const destination = (cible) => {
  if (!cible) return null;
  const id = String(cible.id || "");
  if (cible.onglet === "eleves" && id) return ["eleves", { fiche: id }];
  if (cible.onglet === "liste" && id) return ["journee", { cours: id, le: cible.le || "" }];
  if (cible.onglet === "messages") return ["messages"];
  // Une réservation de cours privé (adresse rejetée d'une famille qui n'a que ça) : l'onglet Privés.
  if (cible.onglet === "cours-prives") return ["prives"];
  if (cible.onglet === "facturation" && id) {
    const cle = id.includes("::") ? id.split("::").pop() : id;
    // `facture` : avec la nouvelle barre, l'alerte ouvre la facture dans l'onglet Factures
    // (EcoleApp) ; sinon, la famille dans Élèves.
    return cle.includes("@") ? ["eleves", { q: cle, facture: id }] : null;
  }
  return null;
};

const GroupeAlertes = ({ g, onChange, onArgent, aller, ouvertAuDepart = false }) => {
  const [ouvert, setOuvert] = useState(ouvertAuDepart);
  const [vus, setVus] = useState([]);
  const items = g.items.filter((it) => !vus.includes(it.ref));
  const voir = async (it) => {
    setVus((v) => [...v, it.ref]);
    try { await api(`/notifications/${g.id}/${encodeURIComponent(it.ref)}/traite`, { method: "POST" }); onChange(); } catch { /* sans gravité */ }
  };
  // Une facture de cours privé à relancer (« prive::F-…CP-… », Jen 02.11.2026 : un rappel tous
  // les 30 jours) : le rappel part d'ici, d'un tap confirmé — l'onglet Factures ne les montre pas.
  const rappelPrive = async (it) => {
    const numero = String(it.ref).split("::")[1] || "";
    if (!window.confirm(`${it.titre} : envoyer le rappel de paiement de la facture ${numero} (facture jointe, copie à l'école) ?`)) return;
    try {
      const r = await api(`/reservations-privees/factures/${encodeURIComponent(numero)}/rappel`, { method: "POST", body: JSON.stringify({ forcer: false }) });
      setVus((v) => [...v, it.ref]);
      window.alert(r.message);
      onChange();
    } catch (e) { window.alert(e.message); }
  };
  const argent = ["impayes", "paiements_partiels", "impayes_saisons_passees"].includes(g.id);
  return (
    <Groupe testId={`ecole-alerte-${g.id}`}>
      <button onClick={() => setOuvert(!ouvert)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-[#F1F5F9]">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COULEUR[g.gravite] || "#94A3B8" }} />
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold leading-snug">{g.titre}</span>
          <span className="block text-[12.5px] text-[var(--muted)] mt-0.5 line-clamp-2">{g.action}</span>
        </span>
        <Badge couleur={COULEUR[g.gravite] || "#94A3B8"}>{items.length}</Badge>
      </button>
      {ouvert && items.slice(0, 40).map((it) => {
        const vers = destination(it.cible);
        return (
        <div key={it.ref} className="px-4 py-3 flex items-start gap-3">
          <button className="flex-1 min-w-0 text-left" disabled={!vers} onClick={() => vers && aller(...vers)}
            data-testid="ecole-alerte-item">
            <div className="text-[14.5px] leading-snug" style={vers ? { color: "var(--primary)" } : undefined}>
              {it.titre}{vers ? " ›" : ""}
            </div>
            {it.detail && <div className="text-[12.5px] text-[var(--muted)] mt-0.5">{it.detail}</div>}
          </button>
          {g.type_action === "confirmer_especes" && (
            <button onClick={async () => {
              if (!window.confirm(`${it.titre} : l'argent a bien été encaissé ?`)) return;
              await api("/facturation/especes/confirmer", { method: "POST", body: JSON.stringify({ ids: [it.ref] }) });
              setVus((v) => [...v, it.ref]); onChange();
            }} className="btn-ghost text-xs shrink-0" data-testid="ecole-especes-confirmer">
              <Check size={13} /> Encaissé
            </button>
          )}
          {g.type_action === "vu" && (
            <button onClick={() => voir(it)} className="btn-ghost text-xs shrink-0" data-testid="ecole-alerte-vu">
              <Check size={13} /> Vu
            </button>
          )}
          {/* Adresses e-mail rejetées : « Réglé » range la ligne, comme dans la console (la
              ligne part aussi d'elle-même une fois l'adresse corrigée sur la fiche). */}
          {g.type_action === "regle" && (
            <button onClick={() => voir(it)} className="btn-ghost text-xs shrink-0" data-testid="ecole-alerte-regle">
              <Check size={13} /> Réglé
            </button>
          )}
          {g.id === "impayes" && String(it.ref).startsWith("prive::") && (
            <button onClick={() => rappelPrive(it)} className="btn-ghost text-xs shrink-0" data-testid="ecole-prive-rappel">
              <Send size={13} /> Rappel
            </button>
          )}
        </div>
        );
      })}
      {ouvert && argent && (
        <button onClick={onArgent} className="w-full px-4 py-3 text-[14px] font-medium text-left" style={{ color: "var(--primary)" }}>
          Relancer depuis l'onglet Argent →
        </button>
      )}
      {ouvert && !["vu", "regle"].includes(g.type_action) && !argent && (
        <a href="/console" className="block px-4 py-3 text-[13px]" style={{ color: "var(--primary)" }}>
          Le traitement complet se fait dans la console (mieux sur ordinateur) →
        </a>
      )}
    </Groupe>
  );
};

const Message = ({ m, onChange }) => {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState("");
  const [envoi, setEnvoi] = useState("");
  const repondre = async () => {
    setEnvoi("…");
    try {
      await api(`/messages/${encodeURIComponent(m.id)}/repondre`, {
        method: "POST", body: JSON.stringify({ sujet: "Votre message à 1000 ARTS", corps: texte }),
      });
      setEnvoi("Réponse envoyée."); setTexte(""); onChange();
    } catch (e) { setEnvoi(e.message); }
  };
  const traiter = async () => {
    await api(`/messages/${encodeURIComponent(m.id)}/traite?traite=${!m.traite}`, { method: "POST" });
    onChange();
  };
  return (
    <div data-testid="ecole-message">
      <button onClick={() => setOuvert(!ouvert)} className="w-full flex items-start gap-3 px-4 py-3.5 text-left active:bg-[#F1F5F9]">
        <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5" style={{ background: m.traite ? "transparent" : "var(--primary)" }} />
        <span className="flex-1 min-w-0">
          <span className="flex items-baseline justify-between gap-2">
            <span className="text-[15px] font-semibold truncate">{m.nom}</span>
            <span className="text-[11.5px] text-[var(--muted)] shrink-0">{dateCourte(m.recu_le)}</span>
          </span>
          <span className="block text-[12.5px] text-[var(--muted)]">{m.categorie}</span>
          <span className={`block text-[13.5px] mt-1 ${ouvert ? "whitespace-pre-line" : "line-clamp-2"}`}>{m.resume}</span>
        </span>
      </button>
      {ouvert && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {m.telephone && <a href={tel(m.telephone)} className="btn-ghost text-xs"><Phone size={13} /> Appeler</a>}
            {m.email && <a href={`mailto:${m.email}`} className="btn-ghost text-xs"><Mail size={13} /> E-mail</a>}
            <button onClick={traiter} className="btn-ghost text-xs" data-testid="ecole-message-traite">
              <Check size={13} /> {m.traite ? "Remettre à traiter" : "Marquer traité"}
            </button>
          </div>
          {m.email && (
            <div className="space-y-2">
              <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={4}
                className="input-field w-full" placeholder="Ta réponse (partira par e-mail)…" />
              <button onClick={repondre} disabled={!texte.trim()} className="btn-primary text-sm" data-testid="ecole-message-repondre">
                <Send size={14} /> Répondre
              </button>
              {envoi && <p className="text-xs text-[var(--muted)]">{envoi}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Toutes les alertes de la console ; `groupe` ouvre directement celui demandé. */
export const Alertes = ({ groupe, aller, onCompte }) => {
  const [notes, setNotes] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => { api("/notifications").then(setNotes).catch((e) => setErreur(e.message)); }, []);
  if (erreur) return <Vide>{erreur}</Vide>;
  if (!notes) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const groupes = notes.groupes.filter((g) => g.id !== "messages")
    .sort((a, b) => (a.id === groupe ? -1 : b.id === groupe ? 1 : 0));
  if (!groupes.length) return <Vide>Rien à traiter. Bravo !</Vide>;
  return (
    <div className="space-y-4" data-testid="ecole-alertes">
      <p className="text-[13px] text-[var(--muted)] px-1">
        Touche un nom pour ouvrir sa fiche. « Vu » (ou « Réglé ») range la ligne.
      </p>
      {groupes.map((g) => (
        <GroupeAlertes key={g.id} g={g} aller={aller} ouvertAuDepart={g.id === groupe}
          onChange={() => onCompte?.()} onArgent={() => aller("plus", { ecran: "argent" })} />
      ))}
    </div>
  );
};

export const Messages = ({ onCompte }) => {
  const [messages, setMessages] = useState(null);
  const [vue, setVue] = useState("a_traiter");
  const [erreur, setErreur] = useState("");
  const charger = useCallback(() => {
    api("/messages").then(setMessages).catch((e) => setErreur(e.message));
    onCompte?.();
  }, [onCompte]);
  useEffect(charger, [charger]);
  if (erreur) return <Vide>{erreur}</Vide>;
  if (!messages) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const liste = messages.items.filter((m) => (vue === "a_traiter" ? !m.traite : m.traite));
  return (
    <div className="space-y-4" data-testid="ecole-messages">
      <Segments testId="ecole-messages-vue" valeur={vue} onChange={setVue} options={[
        { id: "a_traiter", label: `À traiter (${messages.a_traiter})` },
        { id: "traites", label: "Traités" },
      ]} />
      {!liste.length ? <Vide>{vue === "a_traiter" ? "Aucun message en attente." : "Aucun message traité."}</Vide>
        : <Groupe>{liste.slice(0, 60).map((m) => <Message key={m.id} m={m} onChange={charger} />)}</Groupe>}
    </div>
  );
};
